'use client';

import { useTranslation } from 'react-i18next';
import {
  useCurrentUser,
  useMicroSource,
  type MicroProgressSummary,
} from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { SectionShell } from '../_shared/SectionShell';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';
import { cardReset } from '../_shared/styles';

/**
 * Horizontal strip showing the signed-in user's micro-content progress:
 * total completed, in progress, streak days. Sourced from
 * {@code MicroSource.progress.fetchSummary}.
 *
 * <p>Each tile is independently renameable and hideable from the page
 * designer. Only the LABELS are authored — the numbers always come from
 * the progress summary, so an operator can reword a tile but never
 * fabricate its value.
 */
export interface MicroStatsStripProps {
  title?: string;
  signedOutMessage?: string;
  /** Tile captions. Blank falls back to the translated default. */
  completedLabel?: string;
  inProgressLabel?: string;
  streakLabel?: string;
  scoreLabel?: string;
  /** Per-tile visibility. Hiding every tile hides the whole section. */
  showCompleted?: boolean;
  showInProgress?: boolean;
  showStreak?: boolean;
  /** Average score over completed quizzes / scenarios. */
  showScore?: boolean;
}

export function MicroStatsStrip({
  title,
  signedOutMessage: authoredSignedOutMessage,
  completedLabel,
  inProgressLabel,
  streakLabel,
  scoreLabel,
  showCompleted = true,
  showInProgress = true,
  showStreak = true,
  showScore = true,
}: MicroStatsStripProps) {
  const { t } = useTranslation('site-builder-blocks');
  const signedOutMessage = authoredSignedOutMessage ?? t('microStats.signedOut');
  const tiles: TileConfig = {
    completed: { show: showCompleted, label: completedLabel },
    inProgress: { show: showInProgress, label: inProgressLabel },
    streak: { show: showStreak, label: streakLabel },
    score: { show: showScore, label: scoreLabel },
  };
  const user = useCurrentUser<{ name?: string }>();
  const micro = useMicroSource();
  const anyTileVisible = showCompleted || showInProgress || showStreak || showScore;

  const state = useAsyncResource<MicroProgressSummary>(
    async (signal) => {
      // Nothing to show = nothing to ask for. An operator who hid every
      // tile should not cost each visitor an authenticated round-trip.
      if (!anyTileVisible) return null;
      if (!user) return null;
      if (!micro?.progress) return null;
      return await micro.progress.fetchSummary(signal);
    },
    [user, micro, anyTileVisible],
  );

  if (!anyTileVisible) return null;

  if (!user) {
    return (
      <SectionShell title={title}>
        <div
          style={{
            padding: 'var(--space-6, 24px) var(--space-5, 20px)',
            background: 'var(--color-surface-page, #f7f8fa)',
            borderRadius: 'var(--radius-md, 8px)',
            textAlign: 'center',
            color: 'var(--color-text-secondary, #71717a)',
            fontSize: 'var(--font-size-sm, 14px)',
          }}
        >
          {signedOutMessage}
        </div>
      </SectionShell>
    );
  }

  if (!micro?.progress) {
    return (
      <SectionShell title={title}>
        <AdapterPlaceholder blockType="MicroStatsStrip" adapterName="MicroSource.progress" />
      </SectionShell>
    );
  }

  if (state.kind !== 'loaded') {
    return (
      <SectionShell title={title}>
        <Strip
          tiles={tiles}
          completed={state.kind === 'loading' ? '—' : 0}
          inProgress={state.kind === 'loading' ? '—' : 0}
          streak={state.kind === 'loading' ? '—' : 0}
          score="—"
        />
      </SectionShell>
    );
  }

  const { totalCompleted, totalInProgress, streakDays, averageScore } = state.data;
  return (
    <SectionShell title={title}>
      <Strip
        tiles={tiles}
        completed={totalCompleted}
        inProgress={totalInProgress}
        streak={streakDays ?? 0}
        score={formatScore(averageScore)}
      />
    </SectionShell>
  );
}

interface TileConfig {
  completed: { show: boolean; label?: string };
  inProgress: { show: boolean; label?: string };
  streak: { show: boolean; label?: string };
  score: { show: boolean; label?: string };
}

/** Average score as a percentage label; a fraction (0–1) is scaled, an
 *  absent value (no quiz completed yet) shows as a dash. */
function formatScore(value: number | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  const pct = value <= 1 ? value * 100 : value;
  return `${Math.round(pct)}%`;
}

function Strip({
  tiles,
  completed,
  inProgress,
  streak,
  score,
}: {
  tiles: TileConfig;
  completed: number | string;
  inProgress: number | string;
  streak: number | string;
  score: string;
}) {
  const { t } = useTranslation('site-builder-blocks');
  const visible = (
    [
      ['completed', completed, 'microStats.completed'],
      ['inProgress', inProgress, 'microStats.inProgress'],
      ['streak', streak, 'microStats.streak'],
      ['score', score, 'microStats.score'],
    ] as const
  )
    .filter(([key]) => tiles[key].show)
    .map(([key, value, fallbackKey]) => ({
      key,
      value,
      // Blank (not just undefined) also falls back — an operator who
      // clears the field wants the default back, not an unlabeled tile.
      label: tiles[key].label?.trim() || t(fallbackKey),
    }));

  return (
    <div
      style={{
        display: 'grid',
        // Columns follow the visible tiles so hiding one widens the rest
        // instead of leaving a hole.
        gridTemplateColumns: `repeat(${visible.length}, minmax(0, 1fr))`,
        gap: 'clamp(12px, 2vw, 16px)',
      }}
    >
      {visible.map((tile) => (
        <Stat key={tile.key} label={tile.label} value={tile.value} />
      ))}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <Card
      style={{
        ...cardReset,
        padding: 'clamp(16px, 2.5vw, 24px)',
        background: 'var(--color-surface-card, #fff)',
        borderRadius: 'var(--radius-md, 8px)',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          fontSize: 'clamp(32px, 5vw, 48px)',
          fontWeight: 700,
          fontFamily: 'var(--font-family-display, inherit)',
          color: 'var(--color-text-primary, #18181b)',
          lineHeight: 1,
        }}
      >
        {value}
      </div>
      <div
        style={{
          marginTop: 8,
          fontSize: '11px',
          letterSpacing: 'var(--font-tracking-widest, 0.08em)',
          textTransform: 'uppercase',
          color: 'var(--color-text-secondary, #71717a)',
          fontFamily: 'var(--font-family-mono, monospace)',
        }}
      >
        {label}
      </div>
    </Card>
  );
}
